import { Column, Row, Table } from "@tanstack/react-table";
import { pluralize, useDebounceState } from "@theprelude/core";
import {
  Button,
  CloseIcon,
  DownloadIcon,
  Dropdown,
  DropdownCheckbox,
  DropdownContent,
  DropdownLabel,
  DropdownSeparator,
  DropdownTrigger,
  IconButton,
  Input,
  MoreIcon,
  ScrollArea,
  ScrollAreaViewport,
  ScrollBar,
  SearchIcon,
  Truncate,
} from "@theprelude/ds";
import { download, generateCsv, mkConfig } from "export-to-csv";
import { useMemo } from "react";
import { fuzzyFilter } from "@/lib/fuzzy-filters";
import { Highlight } from "../highlight";
import { resetTableVisibility } from "./hooks";

const csvConfig = mkConfig({
  fieldSeparator: ",",
  filename: "export",
  useKeysAsHeaders: true,
});

const isObject = (value: any) => value !== null && typeof value === "object";

const exportCSV = (rows: Row<any>[], columns: Column<any, unknown>[]) => {
  const columnsNames: Record<
    string,
    {
      label: string;
      exportFn?: (data: any) => string;
    }
  > = {};

  for (const column of columns) {
    if (
      column.columnDef.meta?.label &&
      column.columnDef.meta?.canExport !== false
    ) {
      columnsNames[column.id] = {
        label: column.columnDef.meta?.label,
        exportFn: column.columnDef.meta?.exportFn,
      };
    }
  }

  const rowData = rows.map((row) => {
    const data: Record<string, any> = {};
    for (const columnId of Object.keys(columnsNames)) {
      const label = columnsNames[columnId]?.label;
      const exportFn = columnsNames[columnId]?.exportFn;

      if (label) {
        const raw = row.getValue(columnId);
        const value = exportFn ? exportFn(raw) : raw;

        data[label] = isObject(value) ? JSON.stringify(value) : value;
      }
    }

    return data;
  });

  const csv = generateCsv(csvConfig)(rowData);
  download(csvConfig)(csv);
};

function sortColumns(a: Column<any, unknown>, b: Column<any, unknown>) {
  const labelA = a.columnDef.meta?.label ?? "";
  const labelB = b.columnDef.meta?.label ?? "";
  return labelA.localeCompare(labelB);
}

const groupColumnsByControl = (
  columns: Column<any, unknown>[],
): Record<string, Column<any, unknown>[]> => {
  const groupedColumns = columns.reduce<
    Array<{ label: string; columns: Column<any, unknown>[] }>
  >((groups, column) => {
    const columnGroup = column.columnDef.meta?.group ?? "";
    const existingGroup = groups.find((g) => g.label === columnGroup);

    if (existingGroup) {
      existingGroup.columns.push(column);
    } else {
      groups.push({ label: columnGroup, columns: [column] });
    }

    return groups;
  }, []);

  const sortedGroups = groupedColumns.sort((a, b) => {
    const getPriority = (label: string) => {
      if (label === "") return 0;
      if (label === "Control Failures") return 1;
      return 2;
    };
    const priorityA = getPriority(a.label);
    const priorityB = getPriority(b.label);

    if (priorityA !== priorityB) {
      return priorityA - priorityB;
    }

    return a.label.localeCompare(b.label);
  });

  const sortedGroupedColumns = Object.fromEntries(
    sortedGroups.map((group) => [group.label, group.columns.sort(sortColumns)]),
  );
  return sortedGroupedColumns;
};

export function TableOptions({ table }: { table: Table<any> }) {
  const relevantColumns = table.getAllColumns().filter((c) => c.getCanHide());
  const columns = groupColumnsByControl(relevantColumns);

  const canExport = table
    .getAllFlatColumns()
    .some((column) => column.columnDef.meta?.canExport);

  if (relevantColumns.length === 0) {
    return null;
  }

  return (
    <div className="absolute right-0 z-20 flex h-[40px] items-center bg-secondary-background p-2">
      <Dropdown>
        <DropdownTrigger asChild>
          <IconButton icon={<MoreIcon />} />
        </DropdownTrigger>
        <DropdownContent align="end" className="w-full">
          <AdvancedView columns={columns} table={table} />
          {canExport && (
            <>
              <DropdownSeparator />
              <div className="flex items-center justify-between px-3 py-2">
                <ExportButton table={table} canExport={canExport} />
              </div>
            </>
          )}
        </DropdownContent>
      </Dropdown>
    </div>
  );
}

function AdvancedView({
  columns,
  table,
}: {
  columns: Record<string, Column<any, unknown>[]>;
  table: Table<any>;
}) {
  const searchTerm = useDebounceState<string>("", 500);

  const filteredColumns = useMemo(() => {
    return Object.entries(columns).reduce(
      (acc, [groupLabel, groupColumns]) => {
        const filtered = groupColumns.filter((column) => {
          const label = column.columnDef.meta?.label || column.id;
          return fuzzyFilter(
            label.toLowerCase(),
            searchTerm.debouncedValue.toLocaleLowerCase(),
          );
        });

        if (filtered.length > 0) {
          acc[groupLabel] = filtered;
        }
        return acc;
      },
      {} as Record<string, Column<any, unknown>[]>,
    );
  }, [columns, searchTerm.debouncedValue]);

  const allFilteredColumns = Object.values(filteredColumns).flat();
  const allFilteredHideableColumns = allFilteredColumns.filter((column) =>
    column.getCanHide(),
  );
  const isAllFilteredVisible = allFilteredHideableColumns.every((column) =>
    column.getIsVisible(),
  );

  const toggleAllFiltered = (value: boolean) => {
    const nextVisibilityState = Object.fromEntries(
      allFilteredHideableColumns.map((column) => [column.id, value]),
    );
    table.setColumnVisibility((prevState) => ({
      ...prevState,
      ...nextVisibilityState,
    }));
  };

  const visibleColumns = table
    .getAllColumns()
    .filter((column) => column.getIsVisible());

  const canSearch = Object.values(columns).flat().length > 8;

  return (
    <div className="w-[800px]">
      <div className="flex items-center justify-between py-2 pr-3 pl-2">
        <DropdownCheckbox
          className="w-max p-0 pl-1 hover:bg-surface-background"
          checked={isAllFilteredVisible}
          onSelect={(event) => event.preventDefault()}
          onCheckedChange={toggleAllFiltered}
        >
          {visibleColumns.length} {pluralize(visibleColumns.length, "column")}{" "}
          selected
        </DropdownCheckbox>
        {table.options.meta?.name && (
          <RestoreDefault
            tableName={table.options.meta?.name}
            onColumnReset={() => {
              table.resetColumnSizing();
            }}
          />
        )}
      </div>
      <DropdownSeparator />
      {canSearch && (
        <Input
          wrapperClassName="mx-3 mt-2"
          value={searchTerm.value ?? ""}
          onChange={(e) => {
            searchTerm.setDelayed(e.target.value);
          }}
          onKeyDown={(ev) => {
            if (ev.key !== "Escape") {
              ev.stopPropagation();
            }
          }}
          before={<SearchIcon className="text-secondary-foreground" />}
          after={
            <IconButton
              icon={<CloseIcon />}
              onClick={() => {
                searchTerm.setImmediate("");
              }}
            />
          }
          placeholder="Search..."
        />
      )}
      <HorizontalColumnList
        columns={filteredColumns}
        searchValue={searchTerm.debouncedValue}
      />
    </div>
  );
}

function HorizontalColumnList({
  columns,
  searchValue,
}: {
  columns: Record<string, Column<any, unknown>[]>;
  searchValue: string;
}) {
  return (
    <ScrollArea className="mt-2 h-[50vh] w-full px-3 pb-3">
      <ScrollAreaViewport>
        {Object.keys(columns).length > 0 ? (
          <div className="flex gap-2">
            {Object.entries(columns).map(([groupLabel, groupColumns]) => (
              <div key={groupLabel} className="flex w-full flex-col">
                <DropdownLabel className="mb-2 px-0">
                  {groupLabel || "Columns"}
                </DropdownLabel>
                <div className="flex w-full flex-col gap-1">
                  {groupColumns.sort(sortColumns).map((column) => {
                    const label = column.columnDef.meta?.label || column.id;

                    return (
                      <DropdownCheckbox
                        key={column.id}
                        className="min-w-0"
                        checked={column.getIsVisible()}
                        onSelect={(event) => event.preventDefault()}
                        onCheckedChange={(value) =>
                          column.toggleVisibility(!!value)
                        }
                      >
                        <div className="min-w-0">
                          <Truncate text={label}>
                            <Highlight text={label} highlight={searchValue} />
                          </Truncate>
                        </div>
                      </DropdownCheckbox>
                    );
                  })}
                </div>
              </div>
            ))}
          </div>
        ) : (
          <span className="flex justify-center py-2 text-secondary-foreground text-xs">
            No columns found
          </span>
        )}
      </ScrollAreaViewport>
      <ScrollBar orientation="vertical" />
      <ScrollBar orientation="horizontal" />
    </ScrollArea>
  );
}

function RestoreDefault({
  tableName,
  onColumnReset,
}: {
  tableName: string;
  onColumnReset: () => void;
}) {
  return (
    <Button
      className="p-0 text-secondary-foreground text-xs"
      onClick={() => {
        onColumnReset();
        resetTableVisibility(tableName);
      }}
    >
      Reset columns
    </Button>
  );
}

function ExportButton({
  table,
  canExport,
}: {
  table: Table<any>;
  canExport: boolean;
}) {
  if (!canExport) {
    return <div></div>;
  }
  return (
    <Button
      className="p-0 text-secondary-foreground text-xs hover:text-primary-foreground"
      onClick={() =>
        exportCSV(table.getFilteredRowModel().rows, table.getAllFlatColumns())
      }
      icon={<DownloadIcon />}
    >
      Export
    </Button>
  );
}
